import { create, type StateCreator } from 'zustand';
import { persist } from 'zustand/middleware';
import type { MapMode } from '@warplan/shared';
import { isViewMode, type ViewMode } from '../lib/view-mode.js';

/**
 * Active "view" mode for the map UI. Local per-user preference (persisted via
 * localStorage). Drives both which icon taxonomy / side panel is shown and
 * whether the page reads from the live backend or the local sandbox store.
 *
 * `lastSandboxFlavour` remembers the most recently used sandbox flavour so
 * the topbar "Sandbox" button can re-enter the same flavour the user left.
 */
interface ActiveModeState {
  viewMode: ViewMode;
  /** Remembered sandbox flavour for the topbar "Sandbox" toggle. */
  lastSandboxFlavour: MapMode;
  setViewMode: (v: ViewMode) => void;
}

function sanitizeView(input: unknown): ViewMode {
  if (isViewMode(input)) return input;
  return 'planner';
}

function sanitizeFlavour(input: unknown): MapMode {
  return input === 'operation' ? 'operation' : 'planner';
}

const initializer: StateCreator<ActiveModeState> = (set) => ({
  viewMode: 'planner',
  lastSandboxFlavour: 'planner',
  setViewMode: (v: ViewMode): void =>
    set((s) => {
      const next = sanitizeView(v);
      if (next === 'sandbox-planner' || next === 'sandbox-operation') {
        return {
          viewMode: next,
          lastSandboxFlavour: next === 'sandbox-operation' ? 'operation' : 'planner',
        };
      }
      return { viewMode: next, lastSandboxFlavour: s.lastSandboxFlavour };
    }),
});

export const useActiveMode = create<ActiveModeState>()(
  persist(initializer, {
    name: 'warplan.active-mode',
    version: 2,
    partialize: (s: ActiveModeState) => ({
      viewMode: s.viewMode,
      lastSandboxFlavour: s.lastSandboxFlavour,
    }),
    migrate: (persisted: unknown, fromVersion: number) => {
      const p = (persisted ?? {}) as {
        mode?: unknown;
        viewMode?: unknown;
        lastSandboxFlavour?: unknown;
      };
      if (fromVersion < 2) {
        const legacy = sanitizeFlavour(p.mode);
        return {
          viewMode: legacy,
          lastSandboxFlavour: legacy,
        };
      }
      return {
        viewMode: sanitizeView(p.viewMode),
        lastSandboxFlavour: sanitizeFlavour(p.lastSandboxFlavour),
      };
    },
  }),
);
